import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/packages', label: 'Packages' },
  { to: '/gallery', label: 'Gallery' },
  // Events dropdown is rendered separately
  { to: '/get-in-touch', label: 'Contact' },
];

const EVENT_LINKS = [
  { to: '/agricultural-shows', title: 'Agricultural Shows', desc: 'All-day roaming activations for showgrounds' },
  { to: '/festivals-and-community-events', title: 'Festivals & Events', desc: 'Highly visual encounters and roaming characters' },
  { to: '/schools-and-kinders', title: 'Schools & Kinders', desc: 'Fun, age-appropriate learning experiences' },
  { to: '/national-science-week', title: 'National Science Week', desc: 'Interactive STEM-focused dinosaur programs' },
  { to: '/charity-and-community', title: 'Charity & Community', desc: 'Fundraising and community engagement' },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileEventsOpen, setMobileEventsOpen] = useState(false);
  const location = useLocation();
  const isEventPage = EVENT_LINKS.some((e) => location.pathname === e.to);

  return (
    <header className="sticky top-0 z-50 bg-charcoal/95 backdrop-blur-md border-b border-charcoal-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img
              src="/images/jacklogo.png"
              alt="Jurassic Jack"
              className="h-12 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.slice(0, 4).map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative font-display text-sm uppercase tracking-wider transition-colors duration-200
                    ${isActive ? 'text-amber' : 'text-cream/80 hover:text-amber'}`}
                >
                  {link.label}
                  {isActive && (
                    <motion.div
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-amber"
                    />
                  )}
                </Link>
              );
            })}

            {/* Events Dropdown */}
            <div className="relative group">
              <button
                className={`relative font-display text-sm uppercase tracking-wider transition-colors duration-200 flex items-center gap-1.5
                  ${isEventPage ? 'text-amber' : 'text-cream/80 hover:text-amber'}`}
              >
                Events
                <svg
                  className="w-3 h-3 transition-transform duration-300 group-hover:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {isEventPage && (
                  <motion.div
                    layoutId="nav-underline"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-amber"
                  />
                )}
              </button>

              {/* Dropdown Panel */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible
                              group-hover:opacity-100 group-hover:visible
                              transition-all duration-200 z-[1000]">
                <div className="w-72 bg-charcoal border border-charcoal-700 rounded-lg shadow-2xl overflow-hidden">
                  {EVENT_LINKS.map((evt) => (
                    <Link
                      key={evt.to}
                      to={evt.to}
                      className="block px-5 py-3.5 hover:bg-jungle-900 transition-colors border-b border-charcoal-700 last:border-b-0"
                    >
                      <span className="block font-display text-sm font-semibold text-cream">{evt.title}</span>
                      <span className="block text-sm text-cream/50 mt-0.5">{evt.desc}</span>
                    </Link>
                  ))}
                  <div className="border-t border-charcoal-700">
                    <Link
                      to="/get-in-touch"
                      className="block px-5 py-3 bg-orange-vibrant/90 hover:bg-orange-vibrant text-cream text-center
                                 font-display text-sm uppercase tracking-wider font-bold transition-colors"
                    >
                      Event Enquiry
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact link */}
            {NAV_LINKS.slice(4).map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative font-display text-sm uppercase tracking-wider transition-colors duration-200
                    ${isActive ? 'text-amber' : 'text-cream/80 hover:text-amber'}`}
                >
                  {link.label}
                  {isActive && (
                    <motion.div
                      layoutId="nav-underline"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-amber"
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+61414995458"
              className="flex items-center gap-2 text-cream hover:text-amber hover:scale-105 transition-all duration-200 min-h-[44px] px-2"
              aria-label="Call 0414 995 458"
            >
              <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="font-mono text-lg font-semibold">0414 995 458</span>
            </a>
            <Link
              to="/contact"
              className="btn-primary text-sm animate-pulse-amber"
            >
              Request a Quote
            </Link>
          </div>

          {/* Mobile Phone + Hamburger */}
          <div className="flex items-center gap-2 md:hidden">
            <a
              href="tel:+61414995458"
              className="p-2 text-cream hover:text-amber transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Call 0414 995 458"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </a>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-cream hover:text-amber transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-72 bg-charcoal z-50 md:hidden
                         flex flex-col shadow-2xl"
            >
              <div className="flex items-center justify-between p-4 border-b border-jungle-700">
                <span className="font-display text-lg font-bold text-cream">
                  Jurassic <span className="text-orange-vibrant">Jack</span>
                </span>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 text-cream/70 hover:text-cream"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="flex flex-col p-4 gap-1">
                {NAV_LINKS.slice(0, 4).map((link, i) => {
                  const isActive = location.pathname === link.to;
                  return (
                    <motion.div
                      key={link.to}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <Link
                        to={link.to}
                        onClick={() => setMobileOpen(false)}
                        className={`block px-4 py-3 rounded-lg font-display uppercase tracking-wider text-sm transition-colors
                          ${isActive
                            ? 'bg-amber/10 text-amber border-l-2 border-amber'
                            : 'text-cream/80 hover:bg-jungle-800 hover:text-cream'
                          }`}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  );
                })}

                {/* Events expandable section */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <button
                    onClick={() => setMobileEventsOpen(!mobileEventsOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-display uppercase tracking-wider text-sm transition-colors
                      ${isEventPage
                        ? 'bg-amber/10 text-amber border-l-2 border-amber'
                        : 'text-cream/80 hover:bg-jungle-800 hover:text-cream'
                      }`}
                  >
                    Events
                    <svg
                      className={`w-4 h-4 transition-transform duration-300 ${mobileEventsOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <AnimatePresence>
                    {mobileEventsOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-6 pt-1 pb-2 flex flex-col gap-1">
                          {EVENT_LINKS.map((evt) => (
                            <Link
                              key={evt.to}
                              to={evt.to}
                              onClick={() => setMobileOpen(false)}
                              className={`block px-4 py-2.5 rounded-lg text-sm transition-colors
                                ${location.pathname === evt.to
                                  ? 'text-amber bg-amber/10'
                                  : 'text-cream/70 hover:bg-jungle-800 hover:text-cream'
                                }`}
                            >
                              {evt.title}
                            </Link>
                          ))}
                          <Link
                            to="/get-in-touch"
                            onClick={() => setMobileOpen(false)}
                            className="block px-4 py-2.5 mt-1 rounded-lg bg-orange-vibrant/90 text-cream text-center
                                       font-display text-sm uppercase tracking-wider font-bold"
                          >
                            Event Enquiry
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Contact link */}
                {NAV_LINKS.slice(4).map((link, i) => {
                  const isActive = location.pathname === link.to;
                  return (
                    <motion.div
                      key={link.to}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 + i * 0.05 }}
                    >
                      <Link
                        to={link.to}
                        onClick={() => setMobileOpen(false)}
                        className={`block px-4 py-3 rounded-lg font-display uppercase tracking-wider text-sm transition-colors
                          ${isActive
                            ? 'bg-amber/10 text-amber border-l-2 border-amber'
                            : 'text-cream/80 hover:bg-jungle-800 hover:text-cream'
                          }`}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  );
                })}
              </nav>
              <div className="mt-auto p-4">
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="btn-primary w-full text-center text-sm"
                >
                  Request a Quote
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
